'use client';

import { useCallback, useEffect, useState } from 'react';
import { ArticleDocument } from '@/prismicio-types';
import { Content } from '@prismicio/client';
import { useQueryState } from 'nuqs';
import { motion } from 'motion/react';
import { resourcesListSchema } from '@/lib/schema/resourceList';
import { mapPrismicArticle } from '@/utils/common';
import ArticleCard from './articleCard';
import getProfessionalArticles from '@/actions/get-professional-articles';

function ProfessionalArticle({ slug }: { slug: string }) {
    const [articles, setArticles] = useState<ArticleDocument<string>[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [searchQuery] = useQueryState('search', { shallow: false, defaultValue: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const sortOrder = 'latest';

    const ITEMS_PER_PAGE = 12;

    const fetchResources = useCallback(
        async (
            page: number = 1,
            reset: boolean = false
        ) => {
            if (!slug) {
                setArticles([]);
                setHasMore(false);
                return;
            }

            setLoading(true);
            try {
                const result = await getProfessionalArticles({
                    slug,
                    limit: ITEMS_PER_PAGE,
                    page,
                    sortOrder,
                });

                if (reset) {
                    setArticles(result.articles?.results || []);
                } else {
                    if (result.articles?.results) {
                        setArticles((prev) =>
                            prev
                                ? [...prev, ...result.articles!.results]
                                : result.articles!.results
                        );
                    }
                }

                const totalPages = result.articles?.total_pages || 0;
                setHasMore(page < totalPages);
            } catch {
                if (reset) {
                    setArticles([]);
                }
            } finally {
                setLoading(false);
            }
        },
        [slug, sortOrder]
    );

    useEffect(() => {
        fetchResources(1, true);
        setCurrentPage(1);
    }, [slug, fetchResources]);

    // Filter items based on search query
    const filteredArticles = articles?.filter(
        (article) =>
            article.data.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            article.data.description
                ?.toString()
                .toLowerCase()
                .includes(searchQuery?.toLowerCase())
    );

    // Combine and sort items for display
    const combinedItems: Content.ArticleDocument<string>[] = [
        ...(filteredArticles || [])?.sort(
            (a, b) => new Date(b.first_publication_date).getTime() - new Date(a.first_publication_date).getTime()
        )
    ];

    const loadMore = () => {
        if (!loading && hasMore) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            fetchResources(nextPage, false);
        }
    };

    if (!slug || !combinedItems || combinedItems?.length === 0) {
        return null;
    }

    return (
        <div className="py-4 md:py-6">
            <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Articles
                </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {combinedItems.map((article: ArticleDocument<string>) => (
                    <ArticleCard key={article.id} article={mapPrismicArticle(article, '')} />
                ))}
            </div>

            {hasMore && !loading && combinedItems.length > 0 && (
                <motion.div
                    className="flex justify-center mt-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                >
                    <button
                        onClick={loadMore}
                        disabled={loading}
                        className="px-6 py-3 bg-primary text-white rounded-[30px] font-secondary font-medium hover:bg-signal/90 transition-colors disabled:opacity-50"
                    >
                        {loading ? 'Loading...' : 'Load More'}
                    </button>
                </motion.div>
            )}

            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(resourcesListSchema(combinedItems)),
                }}
            />
        </div>
    );
}

export default ProfessionalArticle;
